import { isBinaryFile } from './is-binary-file';
import { NOOP } from './noops';
import { nodeStreamToBuffer } from './stream';
import type { AppFileEntry } from '~/types/files';
import type { FileEntry } from '~/types/git';

const decoder = new TextDecoder();

interface FileEntryWithTime extends FileEntry {
  mtime?: number;
}

export async function appFilesFromTarball(tarball: ReadableStream) {
  const now = Date.now();

  const transform = new TransformStream<FileEntry & { mtime?: number }, AppFileEntry>({
    transform(fileEntry, controller) {
      const binary = isBinaryFile(fileEntry.contents);

      if (binary) {
        return;
      }

      controller.enqueue({
        type: 'file',
        fullPath: fileEntry.path,
        isBinary: false,
        lastModified: fileEntry.mtime ?? now,
        name: fileEntry.path.split('/').at(-1)!,
        contents: decoder.decode(fileEntry.contents),
      });
    },
  });

  (await filesFromTarball(tarball)).pipeTo(transform.writable);

  return transform.readable;
}

/**
 * Returns a stream of file entries from the tarball, ignoring everything that is not a regular file.
 * Cancellation is signaled by emitting `null` and closing the stream.
 */
export async function filesFromTarball(
  tarball: ReadableStream | Promise<ReadableStream>,
): Promise<ReadableStream<FileEntry>>;
export async function filesFromTarball(
  tarball: ReadableStream | Promise<ReadableStream>,
  { mtime }: { mtime: true },
): Promise<ReadableStream<FileEntry & { mtime?: number }>>;
export async function filesFromTarball(
  tarball: ReadableStream | Promise<ReadableStream>,
  { signal }: { signal: AbortSignal },
): Promise<ReadableStream<FileEntry | null>>;
export async function filesFromTarball(
  tarball: ReadableStream | Promise<ReadableStream>,
  { signal, mtime }: { signal?: AbortSignal; mtime?: boolean } = {},
) {
  const [stream, tarStream] = await Promise.all([tarball, import('tar-stream')]);

  let controller: ReadableStreamDefaultController<FileEntryWithTime | null>;

  const output = new ReadableStream<FileEntryWithTime | null>({
    start(controller$) {
      controller = controller$;
    },
  });

  // tar-stream does not actually return true streams, we need this flag
  let finished = false;

  const extract = tarStream
    .extract()
    .on('entry', (headers, stream, next) => {
      if (headers.type !== 'file') {
        stream.on('end', () => next()).on('error', next);
        stream.resume();

        return;
      }

      nodeStreamToBuffer(stream).then((contents) => {
        const path = headers.name.replace(/^[^/]+\//, '');

        if (!signal?.aborted) {
          const entry: FileEntryWithTime = { path, contents };

          if (mtime) {
            entry.mtime = headers.mtime?.valueOf();
          }

          controller.enqueue(entry);
        }

        next();
      }, next);

      stream.resume();
    })
    .on('error', (error) => {
      if (!signal?.aborted) {
        finished = true;
        controller.error(error);
      }
    })
    .on('finish', () => {
      if (!signal?.aborted) {
        finished = true;
        controller.close();
      }
    });

  signal?.addEventListener('abort', () => {
    if (!finished) {
      controller.enqueue(null);
      controller.close();
    }
  });

  const decompressor = new DecompressionStream('gzip');

  stream
    .pipeThrough(decompressor, { signal })
    .pipeTo(
      new WritableStream({
        write(chunk) {
          extract.write(chunk);
        },
        close() {
          extract.end();
        },
        abort(reason) {
          extract.destroy(reason);
        },
      }),
    )
    .catch(NOOP);

  return output;
}
